<template>
  <main>
    <section class="hero">
      <div class="hero-content">
        <h2>Empowering Students Through Technology</h2>
        <p>Providing computers and STEM resources to students across Brevard County</p>
        <div class="hero-buttons">
          <router-link to="/request" class="btn btn-primary">Request a Laptop</router-link>
          <a href="https://portal.spacecoaststem.org/login?signup=true" class="btn btn-secondary">Teacher Signup</a>
          <router-link to="/get-involved" class="btn btn-secondary">Get Involved</router-link>
        </div>
      </div>
    </section>

    <section class="mission">
      <div class="container">
        <h2>Our Mission</h2>
        <p class="mission-text">
          At Space Coast STEM, we believe every student deserves access to the technology they need to succeed in today's digital world.
          We provide refurbished laptops to students throughout Brevard County who lack access to computers at home.
        </p>
      </div>
    </section>

    <section class="impact">
      <div class="container">
        <h2>How We Help Students</h2>
        <div class="impact-grid">
          <div class="impact-card">
            <div class="impact-icon">💻</div>
            <h3>Free Laptops</h3>
            <p>Providing refurbished laptops to students who need them most</p>
          </div>
          <div class="impact-card">
            <div class="impact-icon">♻️</div>
            <h3>Environmental Impact</h3>
            <p>Reducing e-waste by giving used computers a second life instead of landfills</p>
          </div>
          <div class="impact-card">
            <div class="impact-icon">🔧</div>
            <h3>Quality Refurbishment</h3>
            <p>Each laptop is thoroughly tested, wiped clean, and loaded with educational software</p>
          </div>
          <div class="impact-card">
            <div class="impact-icon">🐧</div>
            <h3>Linux Education</h3>
            <p>
              Students learn valuable tech skills by using
              <a href="https://linuxmint.com/" target="_blank" rel="noopener noreferrer">Linux Mint</a>,
              a Linux distribution based on the industry standard Ubuntu.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="cta" id="donate">
      <div class="container">
        <h2>Help Us Make a Difference</h2>
        <p>Support our mission by donating money or equipment to help more students</p>
        <div class="cta-buttons">
          <router-link to="/donate" class="btn btn-primary">Learn About Donating</router-link>
          <router-link to="/contact" class="btn btn-secondary">Contact Us</router-link>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
// Home view - no reactive logic needed for static content
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');

.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section */
.hero {
  background: linear-gradient(135deg, var(--primary-color, #2c4a6b) 0%, var(--secondary-color, #3ba3d1) 100%);
  color: var(--white, #ffffff);
  padding: 8rem 2rem;
  text-align: center;
}

.hero-content h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: 700;
  font-family: 'Poppins', sans-serif;
}

.hero-content p {
  font-size: 1.3rem;
  margin-bottom: 2rem;
  opacity: 0.95;
}

.hero-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.hero .btn-primary,
.hero .btn-secondary {
  background: transparent;
  color: var(--white, #ffffff);
  border: 2px solid var(--white, #ffffff);
}

.hero .btn-primary:hover,
.hero .btn-secondary:hover {
  background: var(--white, #ffffff);
  color: var(--primary-color, #2c4a6b);
  transform: translateY(-3px);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

/* Buttons */
.btn {
  display: inline-block;
  padding: 1rem 2rem;
  border-radius: 5px;
  text-decoration: none;
  font-weight: 600;
  transition: all 0.3s;
  border: 2px solid transparent;
}

.btn-primary {
  background: #e8f0f7;
  color: var(--primary-color, #2c4a6b);
  border: 2px solid var(--primary-color, #2c4a6b);
}

.btn-primary:hover {
  background: #d0e3f2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 74, 107, 0.2);
}

.btn-secondary {
  background: #e8f0f7;
  color: var(--primary-color, #2c4a6b);
  border: 2px solid var(--primary-color, #2c4a6b);
}

.btn-secondary:hover {
  background: #d0e3f2;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(44, 74, 107, 0.2);
}

/* Mission Section */
.mission {
  padding: 5rem 2rem;
  background: var(--light-bg, #f5f7fa);
  text-align: center;
}

.mission h2 {
  font-size: 2.5rem;
  margin-bottom: 2rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.mission-text {
  font-size: 1.2rem;
  max-width: 900px;
  margin: 0 auto;
  line-height: 1.8;
  color: var(--text-light, #666666);
}

/* Impact Section */
.impact {
  padding: 5rem 2rem;
}

.impact h2 {
  text-align: center;
  font-size: 2.5rem;
  margin-bottom: 3rem;
  color: var(--primary-color, #2c4a6b);
  font-family: 'Poppins', sans-serif;
}

.impact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
}

.impact-card {
  background: var(--white, #ffffff);
  padding: 2rem;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
}

.impact-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.impact-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.impact-card h3 {
  color: var(--primary-color, #2c4a6b);
  margin-bottom: 1rem;
  font-size: 1.3rem;
  font-family: 'Poppins', sans-serif;
}

.impact-card p {
  color: var(--text-light, #666666);
  line-height: 1.6;
}

.impact-card a {
  color: var(--secondary-color, #3ba3d1);
  text-decoration: none;
}

.impact-card a:hover {
  text-decoration: underline;
}

/* CTA Section */
.cta {
  background: var(--dark-bg, #1f3447);
  color: var(--white, #ffffff);
  padding: 5rem 2rem;
  text-align: center;
}

.cta h2 {
  font-size: 2.5rem;
  margin-bottom: 1rem;
  font-family: 'Poppins', sans-serif;
}

.cta p {
  font-size: 1.2rem;
  margin-bottom: 2rem;
  opacity: 0.9;
}

.cta-buttons {
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
}

.cta .btn-primary {
  background: var(--accent-color, #e94f3a);
  color: var(--white, #ffffff);
  border: none;
}

.cta .btn-secondary {
  border-color: var(--white, #ffffff);
  background: transparent;
  color: var(--white, #ffffff);
}

/* Responsive Design */
@media (max-width: 768px) {
  .hero-content h2 {
    font-size: 2rem;
  }

  .hero-content p {
    font-size: 1.1rem;
  }

  .hero {
    padding: 4rem 2rem;
  }

  .mission h2,
  .impact h2,
  .cta h2 {
    font-size: 2rem;
  }

  .impact-grid {
    grid-template-columns: 1fr;
  }
}
</style>
